import schemaJson from "./session-messages.schema.json" with { type: "json" };
import limitsJson from "./media-limits.json" with { type: "json" };
import { DATA_CHANNEL_NAMES, type DataChannelName } from "./types.ts";

export const SIGNALR_HUB = "rc" as const;
export const SIGNALR_METHOD = "signal" as const;
export const SIGNALR_GROUP_PREFIX = "session:" as const;

const MESSAGE_TYPES = [
  "sdp-offer",
  "sdp-answer",
  "ice-candidate",
  "displays",
  "select-display",
  "command",
  "commandOutput",
  "quality",
  "control.hello",
  "input.pointer",
  "input.key",
  "input.wheel",
  "input.cad",
  "input.cad.result",
  "clipboard.set",
  "file.offer",
  "file.accept",
  "file.chunk",
  "file.complete",
  "file.ack",
  "file.abort",
  "displays.advertise",
  "displays.selected",
] as const;

export type MessageType = (typeof MESSAGE_TYPES)[number];

const SIGNALLING_TYPES = [
  "sdp-offer",
  "sdp-answer",
  "ice-candidate",
  "displays",
  "select-display",
  "command",
  "commandOutput",
  "quality",
] as const;

export type SignallingType = (typeof SIGNALLING_TYPES)[number];

type SchemaDef = {
  properties?: { type?: { const?: string } };
  oneOf?: { $ref: string }[];
};

type SessionSchema = {
  $id: string;
  "x-signalr": { hub: string; method: string; groupPrefix: string };
  "x-dataChannels": string[];
  $defs: Record<string, SchemaDef>;
};

function isSessionSchema(value: unknown): value is SessionSchema {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record.$id === "string" && typeof record.$defs === "object" && record.$defs !== null;
}

if (!isSessionSchema(schemaJson)) {
  throw new Error("session-messages schema copy is not an object");
}

const schema: SessionSchema = schemaJson;

const signalr = schema["x-signalr"];
if (
  signalr.hub !== SIGNALR_HUB ||
  signalr.method !== SIGNALR_METHOD ||
  signalr.groupPrefix !== SIGNALR_GROUP_PREFIX
) {
  throw new Error("schema x-signalr drifted from hub rc, method signal, group session:");
}

const channels = schema["x-dataChannels"];
if (
  channels.length !== DATA_CHANNEL_NAMES.length ||
  channels.some((name, index) => name !== DATA_CHANNEL_NAMES[index])
) {
  throw new Error("schema x-dataChannels drifted from control, clipboard, files, displays");
}

function channelForType(type: string): DataChannelName | undefined {
  if (type === "control.hello" || type.startsWith("input.")) return "control";
  if (type.startsWith("clipboard.")) return "clipboard";
  if (type.startsWith("file.")) return "files";
  if (type.startsWith("displays.")) return "displays";
  return undefined;
}

const typeToDef = new Map<MessageType, string>();
const found = new Set<string>();

for (const [defName, def] of Object.entries(schema.$defs)) {
  const typeConst = def.properties?.type?.const;
  if (typeof typeConst !== "string") continue;
  if (found.has(typeConst)) {
    throw new Error(`schema type ${typeConst} is declared twice`);
  }
  found.add(typeConst);
  if (!(MESSAGE_TYPES as readonly string[]).includes(typeConst)) {
    throw new Error(`schema type ${typeConst} has no message model`);
  }
  typeToDef.set(typeConst as MessageType, defName);
}

for (const type of MESSAGE_TYPES) {
  if (!found.has(type)) {
    throw new Error(`message model ${type} is not in the schema`);
  }
  const signalling = (SIGNALLING_TYPES as readonly string[]).includes(type);
  const channel = channelForType(type);
  if (signalling && channel !== undefined) {
    throw new Error(`message type ${type} is both a signalling envelope and a data-channel message`);
  }
  if (!signalling && channel === undefined) {
    throw new Error(`message type ${type} is not a signalling envelope or a data-channel message`);
  }
}

const signallingDefs = new Set(
  (schema.$defs.signallingEnvelope?.oneOf ?? []).map((item) => item.$ref.split("/").pop()),
);

for (const type of SIGNALLING_TYPES) {
  const defName = typeToDef.get(type);
  if (!defName || !signallingDefs.has(defName)) {
    throw new Error(`signalling type ${type} is missing from signallingEnvelope`);
  }
}

export const MEDIA_LIMITS = limitsJson;

const limitNames = [
  "maxFileBytes",
  "maxConcurrentFileTransfersPerSession",
  "fileChunkBytes",
  "maxClipboardBytes",
  "maxCadPerSessionPerMinute",
  "qualitySampleIntervalSeconds",
  "maxQualitySamplesPerBatch",
  "maxQualitySamplesPerSession",
  "qualityBatchIntervalSeconds",
  "maxQualityPayloadBytes",
] as const;

for (const name of limitNames) {
  const value = MEDIA_LIMITS[name];
  if (!Number.isInteger(value) || value < 0) {
    throw new Error(`media limit ${name} is not a non-negative integer`);
  }
}

export function schemaDocument(): SessionSchema {
  return schema;
}

export function defForMessageType(type: string): string | undefined {
  if (!(MESSAGE_TYPES as readonly string[]).includes(type)) return undefined;
  return typeToDef.get(type as MessageType);
}

export function isSignallingType(type: string): type is SignallingType {
  return (SIGNALLING_TYPES as readonly string[]).includes(type);
}

export function channelForMessageType(type: string): DataChannelName | undefined {
  return channelForType(type);
}

export function sessionGroup(sessionId: string): string {
  return `${SIGNALR_GROUP_PREFIX}${sessionId}`;
}

export function signalrTarget(sessionId: string): {
  hub: typeof SIGNALR_HUB;
  method: typeof SIGNALR_METHOD;
  group: string;
} {
  return {
    hub: SIGNALR_HUB,
    method: SIGNALR_METHOD,
    group: sessionGroup(sessionId),
  };
}
